<template>
  <b-overlay :show="loading">
    <b-alert v-if="unavailable" show variant="warning" class="p-1">
      Your electronic file is not available right now.
    </b-alert>

    <b-card v-else title="Documents shared with you">
      <p class="text-muted">
        These are the documents in your personnel file that you are allowed to see.
        If something is wrong or missing, use "Request correction". You cannot edit the file yourself.
      </p>
      <b-table responsive show-empty empty-text="No documents are visible to you yet." :items="documents" :fields="fields">
        <template #cell(document_date)="data">{{ formatDate(data.value) }}</template>
        <template #cell(actions)="data">
          <b-button size="sm" variant="outline-primary" class="mr-50" @click="open(data.item)">View</b-button>
          <b-button size="sm" variant="flat-secondary" @click="startCorrection(data.item)">Request correction</b-button>
        </template>
      </b-table>
      <b-button v-if="nextCursor" variant="flat-primary" size="sm" @click="load(true)">Load more</b-button>
    </b-card>

    <b-card v-if="!unavailable" title="Who has viewed your file">
      <b-table responsive show-empty empty-text="No access recorded." :items="accessLog" :fields="logFields">
        <template #cell(at)="data">{{ formatDate(data.value) }}</template>
      </b-table>
    </b-card>

    <b-modal v-model="showCorrection" title="Request a correction" ok-title="Send" @ok="sendCorrection">
      <b-form-textarea v-model="correctionMessage" rows="5" maxlength="2000" placeholder="Describe what is wrong or missing" />
    </b-modal>
  </b-overlay>
</template>

<script>
import { get } from "lodash";
import { BAlert, BButton, BCard, BFormTextarea, BModal, BOverlay, BTable } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

// P21: the signed-in person's own file. the file provider decides what is subject-visible; nothing
// confidential or restricted is ever returned to this view.
export default {
  components: { BAlert, BButton, BCard, BFormTextarea, BModal, BOverlay, BTable },
  data() {
    return {
      loading: false,
      unavailable: false,
      documents: [],
      accessLog: [],
      nextCursor: null,
      showCorrection: false,
      correctionMessage: "",
      correctionDocument: null,
      fields: [
        { key: "title", label: "Title" },
        { key: "category", label: "Category" },
        { key: "document_date", label: "Document date" },
        { key: "actions", label: "" },
      ],
      logFields: [
        { key: "at", label: "When" },
        { key: "actor", label: "Who" },
        { key: "action", label: "What" },
      ],
    };
  },
  created() {
    this.init();
  },
  methods: {
    service() {
      return this.useJwt().mainService.memberEfile;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : "-";
    },
    async init() {
      this.loading = true;
      try {
        await this.load(false);
        const { data } = await this.service().myAccessLog({});
        this.accessLog = (data.data || {}).data || data.data || [];
      } catch (error) {
        this.unavailable = true;
      } finally {
        this.loading = false;
      }
    },
    async load(append) {
      const { data } = await this.service().myDocuments(append && this.nextCursor ? { cursor: this.nextCursor } : {});
      const body = data.data || {};
      this.documents = append ? this.documents.concat(body.data || []) : (body.data || []);
      this.nextCursor = get(body, "pagination.next_cursor") || null;
    },
    async open(doc) {
      const win = window.open("", "_blank");
      try {
        const { data } = await this.service().myLink(doc.id);
        win.location = get(data, "data.url");
      } catch (error) {
        if (win) win.close();
        this.toast("Error!", "danger", "Could not open this document.");
      }
    },
    startCorrection(doc) {
      this.correctionDocument = doc;
      this.correctionMessage = "";
      this.showCorrection = true;
    },
    async sendCorrection(event) {
      if (!this.correctionMessage.trim()) {
        event.preventDefault();
        return;
      }
      try {
        await this.service().requestCorrection({
          message: this.correctionMessage,
          document_id: this.correctionDocument && this.correctionDocument.id,
        });
        this.toast("Sent", "success", "Your request has been sent to HR.");
      } catch (error) {
        this.toast("Error!", "danger", "Could not send your request.");
      }
    },
    toast(title, variant, text) {
      this.$toast({ component: ToastificationContent, props: { title, icon: variant === "success" ? "CheckIcon" : "AlertTriangleIcon", variant, text } });
    },
  },
};
</script>
